import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Table } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';

import { AntSelect, useStatusesChipsGroup } from 'components/common';
import { CompensationStatus, Namespace } from 'enums';
import { Compensation } from 'types';
import { useFilteredCompensations } from 'hooks';

import styles from './CompensationTable.module.scss';
import { useCompensationTable } from './use-compensation-table';

interface CompensationTableProps {
  compensations: Compensation[];
  isLoading?: boolean;
  disableUrlParams?: boolean;
}

export const CompensationTable = ({ compensations, isLoading, disableUrlParams }: CompensationTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(Namespace.PersonalArea);
  const { statuses, selectedStatuses, setSelectedStatuses } = useStatusesChipsGroup();
  const { columns, onSorterChange } = useCompensationTable();
  const filteredCompensations = useFilteredCompensations(compensations, selectedStatuses);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const onRowClick = useCallback(
    (compensation: Compensation) =>
      navigate(compensation.compensationRequestID.toString(), { state: location.pathname + location.search }),
    [location.pathname, location.search, navigate],
  );

  const clearAll = useCallback(() => {
    setSelectedStatuses([]);
    onSorterChange(undefined);
  }, [setSelectedStatuses, onSorterChange]);

  useEffect(() => {
    if (disableUrlParams) return;

    setSearchParams({
      ...(selectedStatuses.length && { selectedStatuses: selectedStatuses.join() }),
    });
  }, [selectedStatuses, setSearchParams, disableUrlParams]);

  useEffect(() => {
    if (disableUrlParams) return;

    const defaultSelectedStatuses: CompensationStatus[] =
      (searchParams.get('selectedStatuses')?.split(',') as CompensationStatus[]) || [];

    setSelectedStatuses(defaultSelectedStatuses);
    // Read URL params only once on mount to restore filters after page reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={styles.tableHeader}>
        <AntSelect
          style={{ minWidth: 250, alignSelf: 'flex-start' }}
          mode="multiple"
          maxTagCount={undefined}
          formItemProps={{ label: t('byStatus') }}
          value={selectedStatuses}
          options={statuses}
          onChange={setSelectedStatuses}
        />

        <Button onClick={clearAll}>{t('clear')}</Button>
      </div>

      <Table
        className={styles.table}
        dataSource={filteredCompensations}
        columns={columns}
        rowKey="compensationRequestID"
        loading={isLoading}
        onRow={(compensation) => ({
          onClick: () => onRowClick(compensation),
        })}
        onChange={(pagination, filters, sorter) => {
          onSorterChange(sorter as SorterResult<Compensation>);
        }}
      />
    </>
  );
};
